import { useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, User, Home } from 'lucide-react';
import { useAuth } from './hooks/useAuth';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import SessionPage from './pages/SessionPage';
import ProfilePage from './pages/ProfilePage';
import SignUpPage from './pages/SignUpPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import FeedbackPage from './pages/FeedbackPage';
import analytics from './lib/tracking';
import { setTelemetryUser, tracer } from './lib/telemetry';

function classifyRoute(pathname: string): string {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/session/')) return 'session';
  if (pathname.startsWith('/auth/')) return 'auth';
  if (pathname === '/feedback') return 'feedback';
  if (pathname === '/profile') return 'profile';
  if (pathname === '/signup') return 'signup';
  return 'other';
}

function App() {
  const { loading, user } = useAuth();
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  // Push the current authenticated user into telemetry so every span is
  // tagged with user.authenticated / user.email.
  useEffect(() => {
    setTelemetryUser(user?.email ? { email: user.email } : null);
  }, [user]);

  // Initialize analytics on component mount
  useEffect(() => {
    analytics.pageView(location.pathname, document.title);
  }, [location.pathname]);

  // Emit a route.navigate span for every router location change. The first
  // render produces a span with route.from = null so we still see the
  // landing path.
  useEffect(() => {
    const span = tracer.startSpan('route.navigate', {
      attributes: {
        // null isn't a valid OTel AttributeValue; omit on the first render.
        'route.from': previousPath.current ?? undefined,
        'route.to': location.pathname,
        'route.kind': classifyRoute(location.pathname),
      },
    });
    span.end();
    previousPath.current = location.pathname;
  }, [location.pathname]);

  // Show loading screen while auth is loading
  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">👋</div>
          <p className="text-text-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const isSessionPage = location.pathname.startsWith('/session/');

  return (
    <div className="min-h-screen bg-app-bg text-text-dark">
      {/* Header - Hide on session pages */}
      {!isSessionPage && <Header />}

      {/* Main Content */}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/session/:sessionId" element={<SessionPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/signin" element={<SignUpPage />} />
          <Route path="/login" element={<SignUpPage />} />
          <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
          {/* Add future routes here */}
        </Routes>
      </main>

      {/* Bottom Navigation - Only show on main app routes, not auth or session pages */}
      {!location.pathname.startsWith('/auth/') && !isSessionPage && (
        <BottomNav />
      )}
    </div>
  );
}

// Bottom Navigation Component
function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const showSignup = import.meta.env.VITE_SHOW_SIGNUP === 'true';

  const tabs = [
    { id: 'home', label: 'Home', icon: Home, path: '/' },
    // { id: 'wholesome', label: 'Wholesome', icon: Heart, path: '/' }, // Navigate to home
    // { id: 'spicy', label: 'Spicy', icon: Flame, path: '/' }, // Navigate to home
    { id: 'feedback', label: 'Feedback', icon: MessageCircle, path: '/feedback' },
    // Show Profile if authenticated, otherwise show Sign Up (if enabled)
    ...(isAuthenticated
      ? [{ id: 'profile', label: 'Profile', icon: User, path: '/profile' }]
      : (showSignup ? [{ id: 'signup', label: 'Sign in', icon: User, path: '/signin' }] : [])
    )
  ];

  const getActiveTab = () => {
    if (location.pathname === '/profile') return 'profile';
    if (location.pathname === '/feedback') return 'feedback';
    if (location.pathname === '/signup' || location.pathname === '/signin' || location.pathname === '/login') return 'signup';
    return 'home'; // Default to home for all other paths
  };

  const activeTab = getActiveTab();

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-accent-pink">
      <div className="flex justify-around py-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                analytics.engagement('bottom_nav_click', 'navigation', {
                  tab: tab.id,
                  from: location.pathname,
                  to: tab.path
                });
                navigate(tab.path);
              }}
              className={`flex flex-col items-center gap-1 px-4 py-2 transition-colors ${
                activeTab === tab.id ? 'text-primary-pink' : 'text-gray-500'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default App;
